import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import Button from "../../../componentes/button/button";
import ContasAPagarService from "../../../services/resource/contasAPagarService";
import { formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import LancamentoFortesCard, { situacaoPagamento } from "./LancamentoFortesCard";

const service = new ContasAPagarService();

const PERFIS_PERMITIDOS = ["ADMIN", "SUPRIMENTOS"];

const QUANTIDADES = [10, 20, 30];

const INPUT = "px-2 py-1.5 border border-input rounded-lg text-sm bg-card text-foreground";

function Situacao({ situacao }) {
    if (!situacao) return <span className="text-muted-foreground">-</span>;
    if (situacao === "PAGO") {
        return <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-success/10 text-success">Pago</span>;
    }
    if (situacao === "EM_ABERTO") {
        return <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-muted text-muted-foreground">Em aberto</span>;
    }
    return (
        <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-700">
            Parcial {situacao.split(":")[1]}
        </span>
    );
}

/**
 * Os perfis do usuário chegam do token como texto ("[ADMIN, SUPRIMENTOS]"), não como lista — por isso são
 * normalizados antes da comparação (que é pelo nome exato do perfil).
 */
export function perfisDoUsuario(user) {
    const bruto = user?.perfis;
    if (Array.isArray(bruto)) return bruto.map(p => String(p).trim());
    if (typeof bruto === "string") {
        return bruto.split(/[,[\]]/).map(p => p.trim()).filter(Boolean);
    }
    return [];
}

export function podeConsultarFornecedorNoFortes(user) {
    return perfisDoUsuario(user).some(p => PERFIS_PERMITIDOS.includes(p));
}

function vencimentos(lancamento) {
    const datas = (lancamento.parcelas || [])
        .filter(p => !p.dataCancelamento && p.data)
        .map(p => formatarData(p.data));
    if (datas.length === 0) return "-";
    return datas.length > 3 ? `${datas.slice(0, 3).join(", ")} (+${datas.length - 3})` : datas.join(", ");
}

/**
 * Botão (ao lado do CPF/CNPJ do fornecedor) que abre a busca de lançamentos no contas a pagar do Fortes.
 * Ao abrir, traz os últimos lançamentos do fornecedor da solicitação; os filtros (fornecedor, vencimento e
 * nota fiscal) permitem refinar ou buscar outros lançamentos.
 */
export default function UltimosLancamentosFortes({ documento, fornecedorNome }) {
    const digitos = (documento || "").replace(/\D/g, "");
    const nomeInicial = fornecedorNome || "";

    const [aberto, setAberto] = useState(false);
    const [limite, setLimite] = useState(10);
    const [fornecedor, setFornecedor] = useState(nomeInicial);
    const [vencimentoDe, setVencimentoDe] = useState("");
    const [vencimentoAte, setVencimentoAte] = useState("");
    const [notaFiscal, setNotaFiscal] = useState("");
    const [lancamentos, setLancamentos] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);
    const [buscou, setBuscou] = useState(false);
    const [expandido, setExpandido] = useState(null);
    const requisicao = useRef(0);

    const buscar = useCallback((filtros) => {
        const numero = ++requisicao.current;
        setCarregando(true);
        setErro(null);
        // Fornecedor igual ao da solicitação: busca pelo CNPJ/CPF (exato). Alterado: busca pelo nome digitado.
        const mesmoFornecedor = filtros.fornecedor.trim().toUpperCase() === nomeInicial.trim().toUpperCase()
            && digitos !== "";
        service.buscarLancamentosNoFortes({
            documento: mesmoFornecedor ? digitos : null,
            fornecedor: mesmoFornecedor ? null : filtros.fornecedor.trim() || null,
            vencimentoInicial: filtros.vencimentoDe || null,
            vencimentoFinal: filtros.vencimentoAte || null,
            notaFiscal: filtros.notaFiscal.trim() || null,
            limite: filtros.limite,
        })
            .then(res => {
                if (numero !== requisicao.current) return;
                setLancamentos(res.data || []);
                setExpandido(null);
                setBuscou(true);
            })
            .catch(e => {
                if (numero !== requisicao.current) return;
                setLancamentos([]);
                setBuscou(true);
                setErro(e?.response?.data?.message || "Não foi possível consultar o Fortes agora.");
            })
            .finally(() => { if (numero === requisicao.current) setCarregando(false); });
    }, [digitos, nomeInicial]);

    // Ao abrir: os últimos lançamentos do fornecedor da solicitação (sem outros filtros).
    useEffect(() => {
        if (!aberto) return;
        setFornecedor(nomeInicial);
        setVencimentoDe("");
        setVencimentoAte("");
        setNotaFiscal("");
        buscar({ fornecedor: nomeInicial, vencimentoDe: "", vencimentoAte: "", notaFiscal: "", limite });
        // reexecuta só a cada abertura da janela
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    function pesquisar(e) {
        e?.preventDefault();
        buscar({ fornecedor, vencimentoDe, vencimentoAte, notaFiscal, limite });
    }

    function limpar() {
        setFornecedor(nomeInicial);
        setVencimentoDe("");
        setVencimentoAte("");
        setNotaFiscal("");
        buscar({ fornecedor: nomeInicial, vencimentoDe: "", vencimentoAte: "", notaFiscal: "", limite });
    }

    if (digitos.length !== 11 && digitos.length !== 14) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => setAberto(true)}
                title="Buscar lançamentos deste fornecedor no Fortes"
                className="inline-flex items-center gap-1.5 shrink-0 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-semibold text-primary hover:bg-muted"
            >
                <i className="fa fa-magnifying-glass" /> Buscar Lançamentos
            </button>

            <Dialog
                header="Lançamentos no Fortes"
                visible={aberto}
                onHide={() => setAberto(false)}
                blockScroll
                className="w-[90%]"
                contentClassName="pt-3"
            >
                <form onSubmit={pesquisar} className="flex flex-wrap items-end gap-3 pb-3">
                    <div className="flex flex-col gap-1 min-w-[220px] flex-1">
                        <label className="text-xs font-medium text-muted-foreground">Fornecedor (nome)</label>
                        <input className={INPUT} value={fornecedor} onChange={e => setFornecedor(e.target.value)}
                            placeholder="Nome do fornecedor — vazio busca em todos" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Vencimento de</label>
                        <input type="date" className={INPUT} value={vencimentoDe} max={vencimentoAte || undefined}
                            onChange={e => setVencimentoDe(e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">até</label>
                        <input type="date" className={INPUT} value={vencimentoAte} min={vencimentoDe || undefined}
                            onChange={e => setVencimentoAte(e.target.value)} />
                    </div>
                    <div className="flex flex-col gap-1 w-40">
                        <label className="text-xs font-medium text-muted-foreground">Nota fiscal</label>
                        <input className={INPUT} value={notaFiscal} onChange={e => setNotaFiscal(e.target.value)}
                            placeholder="Nº da nota" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-muted-foreground">Quantidade</label>
                        <select className={INPUT} value={limite} onChange={e => setLimite(Number(e.target.value))}>
                            {QUANTIDADES.map(q => <option key={q} value={q}>{q} últimos</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" label="Buscar" iconLeft="fa fa-magnifying-glass" loading={carregando} />
                        <Button variant="outline" label="Limpar" iconLeft="fa fa-eraser" disabled={carregando} onClick={limpar} />
                    </div>
                </form>
                <p className="text-xs text-muted-foreground pb-3">
                    Ao abrir, mostra os últimos lançamentos de <strong>{nomeInicial || documento}</strong> (CNPJ/CPF {documento}).
                    Altere o nome do fornecedor para buscar outro; o vencimento considera as parcelas do lançamento.
                    Clique em uma linha para ver parcelas e rateio.
                </p>

                {carregando && <p className="py-6 text-center text-sm text-muted-foreground">Consultando o Fortes...</p>}
                {!carregando && erro && <p className="py-6 text-center text-sm text-destructive">{erro}</p>}
                {!carregando && !erro && buscou && lancamentos.length === 0 && (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                        Nenhum lançamento encontrado no Fortes para os filtros informados.
                    </p>
                )}

                {!carregando && lancamentos.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2">Emissão</th>
                                    <th className="px-3 py-2">Fornecedor</th>
                                    <th className="px-3 py-2">Código</th>
                                    <th className="px-3 py-2">Documento</th>
                                    <th className="px-3 py-2">Vencimento(s)</th>
                                    <th className="px-3 py-2">Filial</th>
                                    <th className="px-3 py-2 text-right">Valor</th>
                                    <th className="px-3 py-2">Histórico</th>
                                    <th className="px-3 py-2 text-center">Situação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lancamentos.map(l => (
                                    <Fragment key={l.codigo}>
                                        <tr className="border-t border-border cursor-pointer hover:bg-muted/50 text-xs"
                                            onClick={() => setExpandido(expandido === l.codigo ? null : l.codigo)}>
                                            <td className="px-3 py-2 whitespace-nowrap">{formatarData(l.dataEmissao)}</td>
                                            <td className="px-3 py-2 min-w-[180px]">
                                                <span className="block font-medium">{l.fornecedorNome || "-"}</span>
                                                {l.fornecedorDocumento && (
                                                    <span className="block text-[11px] text-muted-foreground">{l.fornecedorDocumento}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap font-medium">{l.codigo}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">{l.numeroDocumento || "-"}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">{vencimentos(l)}</td>
                                            <td className="px-3 py-2">{l.filialCodigo || "-"}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">{formatarMoeda(l.valor)}</td>
                                            <td className="px-3 py-2 max-w-[240px] truncate" title={l.historico || l.observacao || ""}>
                                                {l.historico || l.observacao || "-"}
                                            </td>
                                            <td className="px-3 py-2 text-center"><Situacao situacao={situacaoPagamento([l])} /></td>
                                        </tr>
                                        {expandido === l.codigo && (
                                            <tr className="border-t border-border bg-muted/20">
                                                <td colSpan={9} className="p-3"><LancamentoFortesCard lancamento={l} /></td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Dialog>
        </>
    );
}
